import styled from 'styled-components';

import { InputText } from 'ui/inputs';
import { NegativeFill, OpenNewWindow16 } from 'ui/icons';
import { colors } from 'ui/theme';

import { SettingsReadOnlyUrl } from '../EditorContentContainer.styled';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { displayContent } from '../content';
import { MediumDataKey } from '../types';
import { getIsEditMode } from '../utils';
import { UrlInputFieldIcons } from '../Controls/UrlInputFieldIcons';

const StyledLink = styled.a<{ $hasError: boolean }>`
  ${({ theme }) => theme.styles.body.body2};
  color: ${({ theme, $hasError }) =>
    $hasError ? theme.colors.utilityColors.negative100 : theme.colors.linkColors.link};
  cursor: pointer;
  &:hover {
    color: ${({ theme }) => theme.colors.linkColors.linkHover};
  }
  &:active {
    color: ${({ theme }) => theme.colors.linkColors.linkActive};
  }
  &:focus {
    color: ${({ theme }) => theme.colors.linkColors.linkFocus};
  }
`;

const StyledLinkContainer = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${({ theme }) => theme.spacing.values.quarter};
  align-items: center;
`;

const LinkContent = styled.span`
  text-decoration: underline;
  word-break: break-all;
  margin-right: ${({ theme }) => theme.spacing.values.threeEights};
`;

const StyledInputText = styled(InputText)`
  border-width: ${({ theme, hasError }) => (hasError ? theme.spacing.utils.pxToRem(2) : '')};
`;

type UrlInputFieldProps = {
  mediumDataKey?: MediumDataKey;
  hasError?: boolean;
  id: MediumDataKey;
  inputValue?: string;
  isDisabled?: boolean;
  onChangeHandler: (url: string) => void;
  placeholderText?: string;
};

const NO_ICON = false;

export const UrlInputField = ({
  hasError = false,
  id,
  inputValue = '',
  isDisabled = false,
  onChangeHandler,
  placeholderText = displayContent.placeholderUrl,
  mediumDataKey,
}: UrlInputFieldProps) => {
  const { view } = useQueryParamContext();
  const { isEditMode } = useEditableFieldsContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);

  return (
    <div data-test="url-input-field">
      {isEditModeVisible ? (
        <StyledInputText
          data-test={`${id}-input-text`}
          hasError={hasError}
          hasIcon={NO_ICON}
          id={`${id}-settings-input-text`}
          isDisabled={isDisabled}
          onChange={onChangeHandler}
          placeholder={placeholderText}
          value={inputValue ?? ''}
          width={'100%'}
          Suffix={
            mediumDataKey && !isDisabled && <UrlInputFieldIcons mediumDataKey={mediumDataKey} url={inputValue} />
          }
        />
      ) : (
        <>
          {inputValue ? (
            <StyledLinkContainer>
              {hasError && <NegativeFill color={colors.red100} />}
              <StyledLink
                href={inputValue}
                target="_blank"
                rel="noreferrer noopener"
                $hasError={hasError}
                data-test="media-url-input-value"
              >
                <LinkContent>{inputValue}</LinkContent>
                <OpenNewWindow16 isColorInherited />
              </StyledLink>
            </StyledLinkContainer>
          ) : (
            <SettingsReadOnlyUrl isUnset={!inputValue} data-test="media-url-ro">
              {displayContent.strUnsetText}
            </SettingsReadOnlyUrl>
          )}
        </>
      )}
    </div>
  );
};
